import "./AssetDataOrganisationalAttributes.scss"
import { FC } from "react"
import { useTranslation } from "react-i18next"

import { ISummarySection } from "@/types"
import { IListItem, ListItemType } from "components/List/List"
import Summary from "components/Summary/Summary"
import useAssetFields, { AssetField } from "hooks/useAssetFields"
import { AssetStep } from "pages/AssetPage/config"
import TenantEmployee from "models/TenantEmployee"
import { IUseAssetInitialSaleOrRental } from "hooks/useAssetInitialSaleOrRental"
import { useAssetDataContext } from "./AssetDataContext"
import Button from "components/Button/Button"
import Input from "components/Input/Input"
import NoValue from "components/NoValue/NoValue"
import { Flex } from "components/Flex"
import { useAssetPinnedFields } from "stores/assetPinnedFields"

interface IAssetDataOrganisationalAttributesProps {
    tenantEmployees: TenantEmployee[] | undefined
    initialSaleOrRental?: IUseAssetInitialSaleOrRental
}

const AssetDataOrganisationalAttributes: FC<IAssetDataOrganisationalAttributesProps> = ({
    tenantEmployees,
    initialSaleOrRental,
}) => {
    const { t } = useTranslation()
    const { asset, article, pinnable, getEditLink, onEditClick, internalNotesEdit } = useAssetDataContext()
    const { getFields } = useAssetFields({ asset, article, tenantEmployees, initialSaleOrRental, pinnable })
    const { pinnedFields, toggleFieldPin } = useAssetPinnedFields()

    const notesToolbar = internalNotesEdit?.canEdit ? (
        internalNotesEdit.editingContext !== 'section' ? (
            <Button
                label={t('common.edit')}
                theme="link"
                onClick={() => internalNotesEdit.startEdit('section')}
                disabled={internalNotesEdit.editingContext === 'pinned'}
            />
        ) : (
            <div onPointerDown={internalNotesEdit.onToolbarPointerDown}>
                <Flex gap="0.8rem" alignItems="center">
                    <Button label={t('common.save')} theme="link" onClick={internalNotesEdit.handleSave} />
                    <div className="asset-data-organisational-attributes__toolbar-divider" />
                    <Button label={t('common.cancel')} theme="link" onClick={internalNotesEdit.handleCancel} />
                </Flex>
            </div>
        )
    ) : undefined

    const internalNotesItem: IListItem = {
        value: internalNotesEdit?.editingContext === 'section' ? (
            <Input
                type="textarea"
                value={internalNotesEdit.notesValue}
                onChange={(e) => internalNotesEdit.setNotesValue(e.target.value)}
                onBlur={internalNotesEdit.handleBlur}
                autoFocus
                maxLength={2500}
            />
        ) : (
            <div className="mb-2">{internalNotesEdit?.savedNotes || <NoValue>{t('asset.noNotes')}</NoValue>}</div>
        ),
        type: ListItemType.verticalSection,
        valueTestId: 'asset-internal-notes',
        pinned: pinnable ? (pinnedFields?.includes(AssetField.internalNotes) || false) : undefined,
        onPinnedToggle: pinnable ? () => toggleFieldPin(AssetField.internalNotes) : undefined,
    }

    const summaryData: ISummarySection<IListItem>[] = [
        {
            title: t('asset.organisationalAttributes'),
            items: getFields([
                AssetField.profitCenter,
                AssetField.purpose,
                AssetField.currentHolder,
            ]),
            subSections: [
                ...asset.buyBack ? [{
                    title: t('asset.buyBack'),
                    items: getFields([
                        AssetField.buyBackCustomer,
                        AssetField.buyBackSalesPerson,
                        AssetField.buyBackReference,
                    ]),
                }] : [],
                ...asset.isBorrowed ? [{
                    title: t('asset.companyWhereBorrowedAsset'),
                    items: getFields([
                        AssetField.lendingCompanyName,
                        AssetField.lendingCompanyAddress,
                        AssetField.lendingCompanyContactPerson,
                        AssetField.lendingCompanyTelephone,
                        AssetField.lendingCompanyEmail,
                    ]),
                }] : [],
                ...initialSaleOrRental?.isVisible ? [{
                    title: t('asset.initialSaleOrRental'),
                    items: getFields([
                        AssetField.relevantForSalesStatistic,
                        AssetField.relatedRequest,
                        AssetField.reportAsset,
                    ]),
                }] : [],
                {
                    title: t('asset.internalNotesTitle'),
                    toolbar: notesToolbar,
                    items: [internalNotesItem],
                },
            ],
            editLink: getEditLink?.(AssetStep.organisationalAttributes),
            onEditClick: onEditClick ? () => onEditClick(AssetStep.organisationalAttributes) : undefined,
        }
    ]


    return (
        <Summary data={summaryData} />
    )
}

export default AssetDataOrganisationalAttributes
